export type MobileInstallPlatform = 'ios' | 'android';

export const PWA_INSTALL_PROMPT_SNOOZE_MS = 36 * 60 * 60 * 1000;

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let capturing = false;

export function startPwaInstallPromptCapture(): void {
  if (capturing) return;
  capturing = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
  });
}

export function detectMobileInstallPlatform(
  userAgent: string,
  navigatorPlatform: string,
  maxTouchPoints: number
): MobileInstallPlatform | null {
  if (/Android/i.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(userAgent)
    || (navigatorPlatform === 'MacIntel' && maxTouchPoints > 1)) return 'ios';
  return null;
}

export function currentMobileInstallPlatform(): MobileInstallPlatform | null {
  return detectMobileInstallPlatform(
    navigator.userAgent,
    navigator.platform,
    navigator.maxTouchPoints
  );
}

export function isStandaloneWebApp(): boolean {
  const iosNavigator = navigator as Navigator & { readonly standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches
    || iosNavigator.standalone === true;
}

export function canPromptPwaInstall(): boolean {
  return deferredInstallPrompt !== null;
}

export async function promptPwaInstall(): Promise<boolean> {
  const prompt = deferredInstallPrompt;
  if (!prompt) return false;
  deferredInstallPrompt = null;
  await prompt.prompt();
  return (await prompt.userChoice).outcome === 'accepted';
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export function isPwaInstallPromptSnoozed(
  dismissedAt: number | null,
  now: number
): boolean {
  if (dismissedAt === null || !Number.isFinite(dismissedAt) || !Number.isFinite(now)) return false;
  const elapsed = now - dismissedAt;
  return elapsed >= 0 && elapsed < PWA_INSTALL_PROMPT_SNOOZE_MS;
}
